// Generative Particle Art Engine for YogaArtMastery
class CanvasArtStudio {
  constructor() {
    this.canvas = null;
    this.ctx = null;
    this.animationId = null;
    this.particles = [];
    this.userPaths = []; // Custom trails drawn by the user
    this.activePose = 'lotus';
    this.flowSpeed = 1.0;
    this.densityMultiplier = 2; // 1 = Low, 2 = Medium, 3 = High
    
    this.isDrawing = false;
    this.mouse = { x: 0, y: 0, prevX: 0, prevY: 0 };
    
    // Core color systems (loaded from CSS styles dynamically or matched)
    this.colors = {
      light: {
        accent1: 'rgba(201, 122, 99, 0.45)', // Terracotta
        accent2: 'rgba(140, 163, 142, 0.45)', // Sage
        accent3: 'rgba(212, 175, 55, 0.45)',  // Sunset Gold
        trail: 'rgba(43, 39, 35, 0.08)'      // Erase alpha
      },
      dark: {
        accent1: 'rgba(217, 136, 112, 0.6)',  // Glowing Terracotta
        accent2: 'rgba(165, 192, 167, 0.6)',  // Ethereal Sage
        accent3: 'rgba(224, 184, 80, 0.6)',   // Radiant Gold
        trail: 'rgba(25, 34, 28, 0.08)'       // Erase alpha
      }
    };
  }

  init(canvasElement) {
    this.canvas = canvasElement;
    this.ctx = this.canvas.getContext('2d');
    
    this.resize();
    window.addEventListener('resize', () => this.resize());
    
    this.setupInteractions();
    this.generateParticles();
    this.animate();
  }

  resize() {
    if (!this.canvas) return;
    const rect = this.canvas.getBoundingClientRect();
    
    // Support retina displays for crisp line rendering
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;
    this.ctx.scale(dpr, dpr);
    
    // Regenerate particles relative to new width and height
    this.generateParticles();
  }

  setPose(poseName) {
    this.activePose = poseName;
    this.generateParticles();
  }

  setSpeed(speedVal) {
    this.flowSpeed = parseFloat(speedVal);
  }

  setDensity(densityVal) {
    this.densityMultiplier = parseInt(densityVal);
    this.generateParticles();
  }

  // Get current active color scheme based on body dark-mode class
  getColorTheme() {
    const isDark = document.body.classList.contains('dark-mode');
    return isDark ? this.colors.dark : this.colors.light;
  }

  // Generate target points for the mathematical pose paths
  getPoseCoordinates(pose, width, height) {
    const points = [];
    const centerX = width / 2;
    const centerY = height / 2 + 30;
    const size = Math.min(width, height) * 0.35;

    switch (pose) {
      case 'lotus':
        // Lotus posture: Base oval, spine axis, folded knee wings, head ring
        // Spine
        for (let i = 0; i < 40; i++) {
          points.push({ x: centerX, y: centerY - size * (i / 40) });
        }
        // Head
        for (let i = 0; i < 360; i += 10) {
          const rad = (i * Math.PI) / 180;
          points.push({
            x: centerX + (size * 0.12) * Math.cos(rad),
            y: centerY - size + (size * 0.12) * Math.sin(rad)
          });
        }
        // Base / Legs
        for (let i = 0; i < 80; i++) {
          const t = (i / 80) * Math.PI;
          points.push({
            x: centerX + size * 0.8 * Math.cos(t),
            y: centerY + size * 0.15 * Math.sin(t)
          });
        }
        // Right Lotus Petal/Knee
        for (let i = 0; i < 40; i++) {
          const t = (i / 40) * Math.PI * 1.5;
          points.push({
            x: centerX + size * 0.5 * Math.cos(t),
            y: centerY - size * 0.2 + size * 0.4 * Math.sin(t)
          });
        }
        // Left Lotus Petal/Knee (mirrored)
        for (let i = 0; i < 40; i++) {
          const t = (i / 40) * Math.PI * 1.5;
          points.push({
            x: centerX - size * 0.5 * Math.cos(t),
            y: centerY - size * 0.2 + size * 0.4 * Math.sin(t)
          });
        }
        break;

      case 'warrior':
        // Warrior II: Lunge legs, wide horizontal arms, torso
        // Ground leg lunge (Left leg bent, right leg straight)
        // Left thigh bent
        for (let i = 0; i < 25; i++) {
          points.push({ x: centerX - size * 0.7 * (i / 25), y: centerY + size * 0.15 });
        }
        // Left calf down
        for (let i = 0; i < 25; i++) {
          points.push({ x: centerX - size * 0.7, y: centerY + size * 0.15 + size * 0.35 * (i / 25) });
        }
        // Right leg straight back
        for (let i = 0; i < 40; i++) {
          const ratio = i / 40;
          points.push({
            x: centerX + size * 0.8 * ratio,
            y: centerY + size * 0.5 * ratio
          });
        }
        // Torso standing
        for (let i = 0; i < 35; i++) {
          points.push({ x: centerX, y: centerY - size * 0.6 * (i / 35) });
        }
        // Horizontal Arms (Left arm straight out, Right arm straight out)
        for (let i = 0; i < 40; i++) {
          points.push({ x: centerX - size * 0.8 * (i / 40), y: centerY - size * 0.4 });
          points.push({ x: centerX + size * 0.8 * (i / 40), y: centerY - size * 0.4 });
        }
        // Head
        for (let i = 0; i < 360; i += 15) {
          const rad = (i * Math.PI) / 180;
          points.push({
            x: centerX + (size * 0.1) * Math.cos(rad),
            y: centerY - size * 0.7 + (size * 0.1) * Math.sin(rad)
          });
        }
        break;

      case 'tree':
        // Tree Pose: Central trunk, vertical line, branches meeting overhead, bent foot triangle
        // Standing Leg
        for (let i = 0; i < 50; i++) {
          points.push({ x: centerX, y: centerY + size * 0.5 * (i / 50) });
        }
        // Trunk / Spine
        for (let i = 0; i < 40; i++) {
          points.push({ x: centerX, y: centerY - size * 0.7 * (i / 40) });
        }
        // Folded foot resting on standing thigh (triangle shape)
        for (let i = 0; i < 25; i++) {
          const r = i / 25;
          // Knee pointing out to the right
          points.push({ x: centerX + size * 0.4 * r, y: centerY - size * 0.1 * r });
        }
        for (let i = 0; i < 25; i++) {
          const r = i / 25;
          // Knee back to thigh
          points.push({ x: centerX + size * 0.4 * (1 - r), y: centerY - size * 0.1 + size * 0.25 * r });
        }
        // Arms arched overhead
        for (let i = 0; i < 40; i++) {
          const rad = Math.PI + (i / 40) * Math.PI;
          points.push({
            x: centerX + size * 0.3 * Math.cos(rad),
            y: centerY - size * 0.7 + size * 0.45 * Math.sin(rad)
          });
        }
        // Head
        for (let i = 0; i < 360; i += 15) {
          const rad = (i * Math.PI) / 180;
          points.push({
            x: centerX + (size * 0.09) * Math.cos(rad),
            y: centerY - size * 0.5 + (size * 0.09) * Math.sin(rad)
          });
        }
        break;

      case 'cobbler':
        // Cobbler pose: wide leg diamonds, straight spine
        // Spine
        for (let i = 0; i < 50; i++) {
          points.push({ x: centerX, y: centerY - size * 0.8 * (i / 50) });
        }
        // Left leg diamond
        for (let i = 0; i < 30; i++) {
          const r = i / 30;
          points.push({ x: centerX - size * 0.6 * r, y: centerY + size * 0.15 * r });
        }
        for (let i = 0; i < 30; i++) {
          const r = i / 30;
          points.push({ x: centerX - size * 0.6 * (1 - r), y: centerY + size * 0.15 + size * 0.15 * r });
        }
        // Right leg diamond (mirrored)
        for (let i = 0; i < 30; i++) {
          const r = i / 30;
          points.push({ x: centerX + size * 0.6 * r, y: centerY + size * 0.15 * r });
        }
        for (let i = 0; i < 30; i++) {
          const r = i / 30;
          points.push({ x: centerX + size * 0.6 * (1 - r), y: centerY + size * 0.15 + size * 0.15 * r });
        }
        // Head
        for (let i = 0; i < 360; i += 15) {
          const rad = (i * Math.PI) / 180;
          points.push({
            x: centerX + (size * 0.11) * Math.cos(rad),
            y: centerY - size * 0.8 + (size * 0.11) * Math.sin(rad)
          });
        }
        break;

      case 'bridge':
        // Bridge Pose: Large arch, feet/shoulder groundings
        // Big Arch
        for (let i = 0; i < 100; i++) {
          const t = Math.PI * (i / 100);
          points.push({
            x: centerX + size * 0.95 * Math.cos(t),
            y: centerY + size * 0.45 - size * 0.65 * Math.sin(t)
          });
        }
        // Ground supporting columns
        for (let i = 0; i < 20; i++) {
          points.push({ x: centerX - size * 0.95, y: centerY + size * 0.45 + size * 0.1 * (i / 20) });
          points.push({ x: centerX + size * 0.95, y: centerY + size * 0.45 + size * 0.1 * (i / 20) });
        }
        break;
    }

    return points;
  }

  generateParticles() {
    if (!this.canvas) return;
    this.particles = [];
    const rect = this.canvas.getBoundingClientRect();
    const targets = this.getPoseCoordinates(this.activePose, rect.width, rect.height);
    if (targets.length === 0) return;

    // Scale particle count based on density slider
    const particleCount = targets.length * this.densityMultiplier * 1.5;

    for (let i = 0; i < particleCount; i++) {
      // Pick a random target from the pose path
      const target = targets[Math.floor(Math.random() * targets.length)];
      
      // Spawn points randomly scattered around canvas
      const px = Math.random() * rect.width;
      const py = Math.random() * rect.height;

      // Select particle color accents randomly
      const randColor = Math.random();
      let colorType = 'accent1';
      if (randColor > 0.4 && randColor <= 0.8) colorType = 'accent2';
      else if (randColor > 0.8) colorType = 'accent3';

      this.particles.push({
        x: px,
        y: py,
        targetX: target.x,
        targetY: target.y,
        colorType: colorType,
        size: Math.random() * 2 + 1,
        // Particle velocity speeds
        vx: 0,
        vy: 0,
        speed: Math.random() * 0.05 + 0.02,
        wobbleSpeed: Math.random() * 0.02 + 0.01,
        wobbleRadius: Math.random() * 5 + 2,
        angle: Math.random() * Math.PI * 2
      });
    }
  }

  setupInteractions() {
    if (!this.canvas) return;

    const getMousePos = (e) => {
      const rect = this.canvas.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      return {
        x: clientX - rect.left,
        y: clientY - rect.top
      };
    };

    const startDraw = (e) => {
      this.isDrawing = true;
      const pos = getMousePos(e);
      this.mouse.x = pos.x;
      this.mouse.y = pos.y;
      this.mouse.prevX = pos.x;
      this.mouse.prevY = pos.y;
    };

    const draw = (e) => {
      if (!this.isDrawing) return;
      const pos = getMousePos(e);
      this.mouse.prevX = this.mouse.x;
      this.mouse.prevY = this.mouse.y;
      this.mouse.x = pos.x;
      this.mouse.y = pos.y;

      // Add segment to custom user-drawn paths
      this.userPaths.push({
        x: this.mouse.x,
        y: this.mouse.y,
        prevX: this.mouse.prevX,
        prevY: this.mouse.prevY,
        life: 1.0, // Fades over time
        colorTheme: Math.random() > 0.5 ? 'accent1' : 'accent3'
      });
    };

    const stopDraw = () => {
      this.isDrawing = false;
    };

    // Desktop Mouse events
    this.canvas.addEventListener('mousedown', startDraw);
    this.canvas.addEventListener('mousemove', draw);
    this.canvas.addEventListener('mouseup', stopDraw);
    this.canvas.addEventListener('mouseleave', stopDraw);

    // Mobile Touch events
    this.canvas.addEventListener('touchstart', startDraw, { passive: true });
    this.canvas.addEventListener('touchmove', draw, { passive: true });
    this.canvas.addEventListener('touchend', stopDraw);
  }

  animate() {
    this.animationId = requestAnimationFrame(() => this.animate());

    const rect = this.canvas.getBoundingClientRect();
    const themeColors = this.getColorTheme();

    // Semi-transparent redraw gives a beautiful trailing/fade effect
    this.ctx.fillStyle = themeColors.trail;
    this.ctx.fillRect(0, 0, rect.width, rect.height);

    // 1. Draw User Custom Paths
    this.ctx.lineWidth = 3;
    this.ctx.lineCap = 'round';
    
    for (let i = this.userPaths.length - 1; i >= 0; i--) {
      const trail = this.userPaths[i];
      trail.life -= 0.015 * this.flowSpeed; // Fade speed

      if (trail.life <= 0) {
        this.userPaths.splice(i, 1);
        continue;
      }

      // Draw custom trail
      const baseAlphaColor = themeColors[trail.colorTheme];
      const rgb = baseAlphaColor.substring(0, baseAlphaColor.lastIndexOf(','));
      this.ctx.strokeStyle = `${rgb}, ${trail.life * 0.75})`;
      
      this.ctx.beginPath();
      this.ctx.moveTo(trail.prevX, trail.prevY);
      this.ctx.lineTo(trail.x, trail.y);
      this.ctx.stroke();
    }

    // 2. Draw Generative Pose Particles
    this.particles.forEach(p => {
      // Add a magnetic/gravity pull towards target pose coordinates
      const dx = p.targetX - p.x;
      const dy = p.targetY - p.y;
      
      p.vx += dx * p.speed * 0.08 * this.flowSpeed;
      p.vy += dy * p.speed * 0.08 * this.flowSpeed;
      
      // Air friction damping
      p.vx *= 0.88;
      p.vy *= 0.88;

      // Update positions
      p.x += p.vx;
      p.y += p.vy;

      // Add a gentle harmonic wobble to simulate organic energy breathing
      p.angle += p.wobbleSpeed * this.flowSpeed;
      const wobbleX = Math.cos(p.angle) * p.wobbleRadius * 0.12;
      const wobbleY = Math.sin(p.angle) * p.wobbleRadius * 0.12;

      // Interactivity: repel particles if mouse is close
      if (this.isDrawing) {
        const mdx = p.x - this.mouse.x;
        const mdy = p.y - this.mouse.y;
        const dist = Math.sqrt(mdx * mdx + mdy * mdy);
        if (dist < 60) {
          const force = (60 - dist) / 60;
          p.x += (mdx / dist) * force * 15 * this.flowSpeed;
          p.y += (mdy / dist) * force * 15 * this.flowSpeed;
        }
      }

      // Draw Particle
      this.ctx.fillStyle = themeColors[p.colorType];
      this.ctx.beginPath();
      this.ctx.arc(p.x + wobbleX, p.y + wobbleY, p.size, 0, Math.PI * 2);
      this.ctx.fill();
    });
  }

  clearCanvas() {
    this.userPaths = [];
    this.generateParticles();
    
    const rect = this.canvas.getBoundingClientRect();
    this.ctx.clearRect(0, 0, rect.width, rect.height);
  }

  saveImage() {
    if (!this.canvas) return;
    
    // Create download link
    const link = document.createElement('a');
    link.download = `YogaArtMastery-${this.activePose}.png`;
    link.href = this.canvas.toDataURL('image/png');
    link.click();
  }

  destroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
  }
}

export const artStudio = new CanvasArtStudio();
